import { useCallback, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Pagination } from '@aimsos/ui';

import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import TraitQuickFilters from '../components/intelligence/TraitQuickFilters.jsx';
import TraitsResults from '../components/intelligence/TraitsResults.jsx';
import TraitsSourceNotice from '../components/intelligence/TraitsSourceNotice.jsx';
import {
  DEFAULT_TRAIT_QUICK_FILTER,
  TRAIT_FILTERS,
  TRAIT_SEARCH_FIELDS,
  TRAIT_SORT_DIRECTION,
  TRAIT_SORT_FIELD,
  TRAIT_SORTS,
  TRAITS_PAGE_SIZE_OPTIONS,
} from '../constants/intelligence.js';
import { ITEM_KINDS } from '../constants/itemSlideOut.js';
import { TAB_KINDS } from '../constants/profileTabs.js';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useItemParam } from '../hooks/useItemParam.js';
import { useListPagination, usePaginationLabels } from '../hooks/useListPagination.js';
import { useTraits } from '../hooks/useTraits.js';
import { isTraitsListSettled, showsTraitQuickFilters, traitsListState } from '../utils/traitsListState.js';

/** Named order first; "Clear all" returns here too. Search covers title and quote. */
const FILTER_DEFAULTS = Object.freeze({
  defaultSortField: TRAIT_SORT_FIELD,
  defaultSortDirection: TRAIT_SORT_DIRECTION,
  searchFields: TRAIT_SEARCH_FIELDS,
});

/**
 * The Traits subtab of Intelligence (CT-5715): details Ori learned about the
 * person from conversations, and where each one came from.
 *
 * Search, sort and All filters are the domain's one toolbar
 * (`DataListFilterBar` + `DataListFilterSlideout` + `useDataListFilters`): the
 * chips and sort commit at once, the All filters panel is STAGED. The quick
 * filter chips are committed state, like Activity's. Everything narrows on the
 * server; nothing here filters rows.
 *
 * What the list is SHOWING is `traitsListState`, a pure function, and
 * `TraitsResults` paints it — the page itself keeps the toolbar, the chips and
 * the pager. The pager is `useListPagination`, the same one the Activity feed
 * and the dynamic list tabs use.
 *
 * A row opens the trait in the profile slide-out by writing `?item=trait:<id>`;
 * the slide-out host in `UnifiedProfilePage` renders it.
 */
function TraitsPage() {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId, entity, tabs = [], openTab, subtabSwitch } = useOutletContext();
  const name = entity?.title ?? t('profile.untitled');

  const [quickFilter, setQuickFilter] = useState(DEFAULT_TRAIT_QUICK_FILTER);
  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);

  const filtersState = useDataListFilters(FILTER_DEFAULTS);
  const { filters, search, orderBy, orderDirection, hasActiveQuery, clearAll } = filtersState;

  const queryKey = `${quickFilter}|${orderBy}|${orderDirection}|${JSON.stringify(filters ?? {})}|${search?.query ?? ''}`;
  const { page, pageSize, handlePrevPage, handleNextPage, handlePageSizeChange } = useListPagination({
    pageSizeOptions: TRAITS_PAGE_SIZE_OPTIONS,
    queryKey,
  });

  const { items, unverified, disconnectedSources, restricted, meta, loading, error, accessDenied, refetch } =
    useTraits({ entityType, entityId, quickFilter, page, pageSize, orderBy, orderDirection, filters, search });

  const { rangeLabel, labels: paginationLabels } = usePaginationLabels({ meta, itemCount: items.length });

  const { item, openItem } = useItemParam();
  const openTraitId = item?.kind === ITEM_KINDS.TRAIT ? item.id : null;
  const handleOpen = useCallback((trait) => openItem(ITEM_KINDS.TRAIT, trait.id), [openItem]);

  const filterDefs = useMemo(
    () =>
      TRAIT_FILTERS.map(({ id, options }) => ({
        id,
        label: t(`intelligence.traits.filters.${id}.label`),
        options: options.map((value) => ({ value, label: t(`intelligence.traits.filters.${id}.options.${value}`) })),
      })),
    [t],
  );
  const sortDefs = useMemo(
    () => TRAIT_SORTS.map((value) => ({ value, label: t(`intelligence.traits.sort.${value}`) })),
    [t],
  );

  const activityTab = useMemo(() => tabs.find((tab) => tab.kind === TAB_KINDS.ACTIVITY), [tabs]);
  const canLogActivity = Boolean(activityTab && openTab);
  // TODO(CT-5724): the History / Activity tab opens its form from `?new=note|call`.
  // Guarded HERE, not only where the buttons render: an entity type whose tabs
  // endpoint returns no Activity tab has no `activityTab.id` to read.
  const openActivity = useCallback(
    (nextSearch) => {
      if (!activityTab || !openTab) return;
      openTab(activityTab.id, nextSearch);
    },
    [activityTab, openTab],
  );
  const addNote = useCallback(() => openActivity('?new=note'), [openActivity]);
  const logCall = useCallback(() => openActivity('?new=call'), [openActivity]);

  const clearEverything = useCallback(() => {
    clearAll();
    setQuickFilter(DEFAULT_TRAIT_QUICK_FILTER);
  }, [clearAll, setQuickFilter]);

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);

  const listState = traitsListState({
    loading,
    accessDenied,
    error,
    itemCount: items.length,
    unverifiedCount: unverified.count,
    restricted,
    narrowed: hasActiveQuery || quickFilter !== DEFAULT_TRAIT_QUICK_FILTER,
  });
  const settled = isTraitsListSettled(listState);
  const showChips = showsTraitQuickFilters(listState);
  const showPagination = settled && meta.totalPages > 1;

  return (
    <div
      data-testid="traits-page"
      data-quick-filter={quickFilter}
      className={['flex min-w-0 flex-1 flex-col gap-4', showPagination ? '' : 'pb-6'].filter(Boolean).join(' ')}
    >
      <div className="flex shrink-0 items-start gap-3">
        {subtabSwitch}
        <div className="min-w-0 flex-1">
          <DataListFilterBar
            filtersState={filtersState}
            filterDefs={filterDefs}
            sortDefs={sortDefs}
            searchPlaceholder={t('intelligence.traits.searchPlaceholder')}
            onAllFiltersClick={openAllFilters}
          />
        </div>
      </div>

      <p className="text-sm text-content-secondary">{t('intelligence.traits.description', { name })}</p>

      {settled ? <TraitsSourceNotice sources={disconnectedSources} /> : null}

      {showChips ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TraitQuickFilters value={quickFilter} onChange={setQuickFilter} />
          {settled ? (
            <span data-testid="traits-count" className="text-sm text-content-muted">
              {t('intelligence.traits.count', { count: meta.totalItems })}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <TraitsResults
          state={listState}
          perPage={pageSize}
          error={error}
          onRetry={refetch}
          items={items}
          unverified={unverified}
          openTraitId={openTraitId}
          onOpenTrait={handleOpen}
          name={name}
          onAddNote={canLogActivity ? addNote : null}
          onLogCall={canLogActivity ? logCall : null}
          onClearFilters={clearEverything}
        />
      </div>

      {showPagination ? (
        <div className="sticky bottom-0 z-10 mt-auto bg-surface-base pb-6">
          <Pagination
            rangeLabel={rangeLabel}
            totalItems={meta.totalItems}
            currentPage={meta.currentPage}
            totalPages={meta.totalPages}
            hasPrevious={meta.hasPrevious}
            hasNext={meta.hasNext}
            perPage={pageSize}
            pageSizeOptions={TRAITS_PAGE_SIZE_OPTIONS}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
            ariaLabelPerPage={t('list.pagination.rowsPerPage')}
            labels={paginationLabels}
          />
        </div>
      ) : null}

      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
      />
    </div>
  );
}

export default TraitsPage;
